import { observable } from 'mobx'
import { EventEmitter } from 'events'
import _ from 'underscore'
import { t } from 'i18next'

import { ApiDotBible } from './ApiDotBible'
import { DBAcceptor } from './DBAcceptor'
import { DBObject } from './DBObject'
import { IDB } from './IDB'
import { findMember, findMemberIndex, Member, MemberRole } from './Member'
import { PassageCopyVisitor } from './PassageCopyVisitor'
import { Portion } from './Portion'
import { ProjectImage } from './ProjectImage'
import { ProjectMessage } from './ProjectMessage'
import { ProjectPlan } from './ProjectPlan'
import { ProjectTerm } from './ProjectTerm'
import { ResourceCollection } from './ResourceCollection'
import { SignVideo } from './SignVideo'
import { move, remove, normalizeUsername } from './Utils'

import { SegmentEditorPanel, SegmentEditorPanelLayout } from '../components/segments/SegmentPanelOrder'
import { fmt } from '../components/utils/Fmt'
import { canAccessInternet } from '../components/app/OnlineStatusContext'

import { convertReferenceStrings } from '../resources/Versifications'
import { bookNamesByLanguage } from '../scrRefs/bookNames'
import { LexMeaning, MarbleLemma } from '../scrRefs/Lemmas'
import ProjectTermCollection from '../scrRefs/ProjectTerms'
import { RefRange } from '../scrRefs/RefRange'
import { TargetGloss } from './TargetGloss'
import { BiblicalTermMarker } from './BiblicalTermMarker'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const log = require('debug')('sltt:Models')

export const DB_ACCEPTOR_VERSION = 21

log(ApiDotBible.versions)

/*
Project
    Member
    Portion
        Passage
            PassageVideo
                PassageSegment
                    PassageSegmentLabel
                    PassageSegmentGloss
                PassageNote
                    PassageNoteItem
                PassageGloss
                
*/

export interface ICopyFileToVideoCache {
    // Copies the file to VideoCache and returns the _id of the resulting video blob
    (file: File, baseUrl: string, creationDate?: string, requestUpload?: boolean): Promise<string>
}

type BookNameMap = { [bbb: string]: string }

export enum DateFormat {
    mmddyyyy = 'MM/dd/yyyy',
    ddmmyyyy = 'dd/MM/yyyy',
    mmmmddyyyy = 'MMMM dd, yyyy',
    yyyymmdd = 'yyyy-MM-dd'
}

export interface IVideoCacheAcceptor {
    // Queue video for download to video cache. See VideoCache.accept
    (_id: string, creationDate: string): Promise<void>
}

interface SetPortionFromExisting {
    sourceProject: Project
    portion: Portion
    includeResources: boolean
    latestDraftOnly: boolean
}
export class Project extends DBObject {
    name: string

    @observable displayName = ''

    @observable text = ''

    initialized = false

    dbAcceptor?: DBAcceptor

    watcher: EventEmitter | null = null

    @observable bookNames: BookNameMap = {}

    @observable portions: Portion[]

    @observable members: Member[]

    @observable plans: ProjectPlan[] = [] // Currently there is only 1 plan

    @observable signs: { [index: string]: SignVideo }

    @observable terms: ProjectTerm[] = []

    @observable termCollection = new ProjectTermCollection()

    @observable copyrightStatement = ''

    @observable noteColors: string[] = ['#418D2B', '#4873A6', '#1F2E69'] // green, light blue, dark blue

    @observable dateFormat: DateFormat = DateFormat.mmddyyyy

    @observable compressedVideoQuality = 20

    @observable compressedVideoResolution = 720

    @observable maxVideoSizeMB = 100

    @observable recordAudioOnly = true

    @observable overrideRecordingLayout = false

    @observable segmentEditorPanelLayout = SegmentEditorPanelLayout

    @observable segmentEditorHiddenPanels: SegmentEditorPanel[] = []

    @observable images: ProjectImage[] = []

    @observable versification = 'English' // do not persist, since this is hardcoded

    @observable messages: ProjectMessage[] = []

    @observable projectType: 'translation' | 'additional' | 'test_training' | 'resource' | 'other' | '' = ''

    @observable region: 'africa' | 'americas' | 'asia' | 'europe' | 'oceania' | '' = ''

    oldPlanDoc: any = undefined

    resourceCollection = new ResourceCollection()

    static copyFileToVideoCache: ICopyFileToVideoCache

    static videoCacheAcceptor: IVideoCacheAcceptor

    constructor(name: string, db: IDB, displayName?: string) {
        super('project', db)

        this.name = name
        this.displayName = displayName || name
        this.db = db
        this.portions = []
        this.members = []
        this.signs = {}

        this.setDisplayName = this.setDisplayName.bind(this)
        this.setText = this.setText.bind(this)
        this.canAddMember = this.canAddMember.bind(this)
        this.addImage = this.addImage.bind(this)
        this.moveImage = this.moveImage.bind(this)
        this.deleteImage = this.deleteImage.bind(this)
        this.setCompressedVideoQuality = this.setCompressedVideoQuality.bind(this)
        this.setCompressedVideoResolution = this.setCompressedVideoResolution.bind(this)
        this.setMaxVideoSizeMB = this.setMaxVideoSizeMB.bind(this)
        this.setBookName = this.setBookName.bind(this)

        for (let i = 0; i < 66; ++i) {
            const bbb = `${i + 1}`.padStart(3, '0')
            this.bookNames[bbb] = bookNamesByLanguage.en[i]
        }
    }

    toDocument() {
        const { members } = this
        return this._toDocument({ members })
    }

    toSnapshot() {
        const snapshot: any = {}

        snapshot.members = this.members.map((member) => member.toSnapshot())
        snapshot.plan = this.plans.map((plan) => plan.toSnapshot())
        snapshot.portions = this.portions.map((portion) => portion.toSnapshot())
        snapshot.booksNames = this.bookNames

        return snapshot
    }

    async initialize(progress: (message: string) => void, allowInvalidIds = false) {
        log('initialize')
        if (this.initialized) return 0

        log('startSync')
        this.dbAcceptor = new DBAcceptor(this, Project.videoCacheAcceptor, DB_ACCEPTOR_VERSION, allowInvalidIds)

        const dbRecordCount = await this.db.initialize(this.dbAcceptor, progress)
        this.dbAcceptor.cleanUp()
        await this.setUpProjectPlan()
        this.resourceCollection.initialize(this)
        this.initialized = true

        return dbRecordCount
    }

    cleanUpTargetGlosses() {
        const deletedGlosses = this.termCollection.removeUnusedTargetGlosses()
        deletedGlosses.forEach((glossId) => {
            this.terms.find((term) => term.glosses.find((gloss) => gloss._id === glossId))?.removeGloss(glossId)
        })
    }

    getResources = (refs: RefRange[]) => {
        return this.resourceCollection.get(refs)
    }

    createPortionFromExisting(portion: Portion) {
        let { name } = portion
        let number = 0
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        while (this.portions.find((p) => p.name === name)) {
            number++
            name = `${portion.name}-${number}`
        }
        const newPassage = this.createPortion(name)
        const copy = portion.copy()
        copy._id = newPassage._id
        copy.name = newPassage.name
        copy.rank = newPassage.rank
        copy.passages = []
        return copy
    }

    createPortion(name: string) {
        name = name.trim()
        if (this.portions.find((p) => p.name === name)) {
            throw Error(`${t('System Error')}: Duplicate name`)
        }

        const newId = this.db.getNewId(this.portions, new Date(Date.now()))
        const portion = new Portion(newId, this.db)

        portion.name = name

        let rank = 100
        if (this.portions.length > 0) {
            rank = this.portions.slice(-1)[0].rankAsNumber + 100
        }
        portion.rank = DBObject.numberToRank(rank)

        return portion
    }

    async addPortion(name: string) {
        name = name.trim()
        if (this.portions.find((p) => p.name === name)) {
            throw Error(`${t('System Error')}: Duplicate name`)
        }

        const newId = this.db.getNewId(this.portions, new Date(Date.now()))
        const portion = new Portion(newId, this.db)

        portion.name = name

        let rank = 100
        if (this.portions.length > 0) {
            rank = this.portions.slice(-1)[0].rankAsNumber + 100
        }
        portion.rank = DBObject.numberToRank(rank)

        await this.db.put(portion.toDocument())

        const _portion = _.findWhere(this.portions, { _id: portion._id }) ?? null
        return _portion
    }

    async addPortionFromExisting(portion: Portion) {
        await this.db.put(portion.toDocument())
        const _portion = _.findWhere(this.portions, { _id: portion._id })
        if (!_portion) throw new Error('could not find newly added Portion')
        return _portion
    }

    async removePortion(_id: string) {
        await remove(this.portions, _id)
    }

    async movePortion(_id: string, i: number) {
        const idx = _.findIndex(this.portions, { _id })
        if (idx === -1) throw Error(`movePortion: _id not found [${_id}]`)
        await move(this.portions, idx, i)
    }

    async setBookName(bbbccc: string, projectBookName: string) {
        if (!Object.keys(this.bookNames).includes(bbbccc) || this.bookNames[bbbccc] === projectBookName) {
            return
        }
        const doc = this.toDocument()
        doc.bbbccc = bbbccc
        doc.projectBookName = projectBookName
        await this.db.put(doc)
    }

    private async setUpProjectPlan() {
        if (this.plans.length === 0) {
            await this.addDefaultProjectPlan()
        }
    }

    async addDefaultProjectPlan() {
        let plan = this.createProjectPlan(new Date(Date.now()))
        await this.db.put(plan.toDocument())
        plan = this.plans[this.plans.length - 1]
        if (!plan) {
            log('Project plan was not successfully added')
            return
        }

        await plan.addDefaultPlan()
    }

    async addProjectPlan() {
        if (this.plans.length > 0) {
            throw Error('Plan already exists')
        }

        const plan = this.createProjectPlan(new Date(Date.now()))
        const doc = plan.toDocument()
        await this.db.put(doc)

        return this.plans[0]
    }

    async setViewableStagesFromExisting(sourceProject: Project) {
        const sourceProjectPlan = sourceProject.plans[0]
        if (!sourceProjectPlan) {
            throw Error(
                t('No plan was found to copy from project {{project}}', {
                    project: sourceProject.getFormattedDisplayName()
                })
            )
        }

        const destinationPlan = this.plans.length ? this.plans[0] : await this.addProjectPlan()
        if (!destinationPlan) {
            throw Error(
                t('No plan was found to copy into project {{projectName}}', {
                    projectName: this.getFormattedDisplayName()
                })
            )
        }

        await this.removeViewableStages()
        for (const stage of sourceProjectPlan.viewableStages) {
            const destinationStage = await destinationPlan.addStage(stage.rankAsNumber, stage.name)
            for (const task of stage.tasks) {
                await destinationStage.addTask(destinationPlan, task.rankAsNumber, task.name, task.details)
            }
        }
    }

    createProjectPlan(creationDate: Date) {
        const newId = this.db.getNewId(this.plans, creationDate, 'plan_')
        const plan = new ProjectPlan(newId, this.db)

        const rank = 100
        plan.rank = DBObject.numberToRank(rank)

        return plan
    }

    async removeViewableStages() {
        const plan = this.plans[0]
        if (!plan) {
            throw Error(
                t('No plan was found for project {{projectName}}', { projectName: this.getFormattedDisplayName() })
            )
        }

        for (const stage of plan.viewableStages) {
            await plan.removeStage(this, stage._id)
        }
    }

    canAddMember(email: string) {
        email = normalizeUsername(email)
        if (email === '') return 'Email address cannot be empty'

        if (findMember(this.members, email)) return 'Duplicate email address'
        if (email.split('@').length !== 2) return 'Invalid email address'

        return ''
    }

    async addMember(email: string, role: MemberRole = 'translator') {
        if (!(await canAccessInternet('addMember'))) {
            throw Error(t('offlineActionError'))
        }

        email = normalizeUsername(email)
        if (findMember(this.members, email)) {
            return
        }

        const doc = this.toDocument()
        const _doc: any = doc
        _doc.members.push({ email, role })

        await this.db.put(_doc)

        // FUTURE: Update only the member that has changed, backend must update project table
        // let doc = this._toDocument({ model: 2, email, role: 'translator' })
        // doc._added = true
        // doc._id = 'member'
        // await this.db.put(doc)
    }

    async removeMember(email: string) {
        const doc = this.toDocument()
        const _doc: any = doc

        const index = findMemberIndex(_doc.members, email)
        if (index < 0) return

        _doc.members.splice(index, 1)

        await this.db.put(_doc)

        // FUTURE: Update only the member that has changed, backend must update project table
        // let doc = this._toDocument({ model: 2, email })
        // doc._removed = true
        // doc._id = 'member'
        // await this.db.put(doc)
    }

    async setMemberRole(email: string, role: MemberRole) {
        log('setmemberRole', email, role)

        const doc = this.toDocument()
        const _doc: any = doc

        const member = findMember(_doc.members, email)
        if (!member) return

        member.role = role

        await this.db.put(_doc)

        // FUTURE: Update only the member that has changed, backend must update project table
        // let member = findMember(this.members, email)
        // if (!member || member.role === role) {
        //     return
        // }
        // let doc = this._toDocument({ model: 2, email, role })
        // doc._id = 'member'
        // await this.db.put(doc)
    }

    async setMemberImage(email: string, imageUrl: string) {
        const member = findMember(this.members, email)
        if (!member || member.imageUrl === imageUrl) {
            return
        }

        const doc = this._toDocument({ model: 2, email, imageUrl })
        doc._id = 'member'
        await this.db.put(doc)
    }

    getDefaultPortion(_id: string | null) {
        const { portions } = this
        let portion = _id && _.findWhere(portions, { _id })

        if (!portion) {
            if (portions.length > 0) portion = portions[0]
        }

        return portion
    }

    async setPortionFromExisting({
        sourceProject,
        portion,
        includeResources,
        latestDraftOnly
    }: SetPortionFromExisting) {
        const newPortion = this.createPortionFromExisting(portion)
        newPortion.copiedFromId = `${sourceProject.name}/${portion._id}`
        const addedPortion = await this.addPortionFromExisting(newPortion)
        const visitor = new PassageCopyVisitor(this, sourceProject, addedPortion, includeResources)

        try {
            // do this sequentially to ensure proper copy
            for (const passage of portion.passages) {
                await visitor.visitPassage(passage, latestDraftOnly)
            }
        } catch (error) {
            await this.removePortion(addedPortion._id)
            throw new Error(`Could not copy portion ${addedPortion._id}
                from project ${sourceProject.getFormattedDisplayName()} to ${this.getFormattedDisplayName()}`)
        }
    }

    async setPortionsFromExisting({
        sourceProject,
        portions,
        includeResources,
        latestDraftOnly
    }: Omit<SetPortionFromExisting, 'portion'> & { portions?: Portion[] }) {
        await Promise.all(
            (portions || sourceProject.portions).map((portion) =>
                this.setPortionFromExisting({ sourceProject, portion, includeResources, latestDraftOnly })
            )
        )
    }

    async sortPortions() {
        const portions = _.sortBy(this.portions, (portion) => portion.name)
        for (let i = 0; i < portions.length; ++i) {
            await portions[i].setRank(100 * (i + 1))
        }
    }

    async logPortions() {
        const { portions } = this
        for (let i = 0; i < portions.length; ++i) {
            const p = portions[i]
            console.log(`${p.name} = ${p.rank}`)
        }
    }

    createSignVideo(lemmaId: string) {
        const _id = `sign/${lemmaId}`
        return new SignVideo(_id, this.db)
    }

    createProjectTerm(lexicalLink: string) {
        const newId = this.db.getNewId(this.terms, new Date(Date.now()), 'term_')
        const term = new ProjectTerm(newId, this.db)
        term.lexicalLink = lexicalLink

        let rank = 100
        if (this.terms.length > 0) {
            rank = this.terms[0].rankAsNumber / 2
        }
        term.rank = DBObject.numberToRank(rank)
        return term
    }

    async addProjectTerm(term: ProjectTerm) {
        await this.db.put(term.toDocument())
        return this.getProjectTerm(term.lexicalLink)
    }

    acceptProjectTerm(term: ProjectTerm) {
        this.termCollection.addProjectTerm(term)
    }

    acceptTargetGloss(gloss: TargetGloss) {
        this.termCollection.addTargetGloss(gloss)
    }

    acceptBiblicalTermMarker(marker: BiblicalTermMarker) {
        this.termCollection.addBiblicalTermMarker(marker)
    }

    acceptDeletedTermMarker(marker: BiblicalTermMarker) {
        this.termCollection.removeBiblicalTermMarker(marker)
    }

    getProjectTerm(lexicalLink: string) {
        return this.termCollection.getProjectTermByLexicalLink(lexicalLink)
    }

    async migrateLemma(lemma: MarbleLemma) {
        const signVideo = this.signs[`sign/${lemma?.id}`]
        if (!signVideo) {
            return
        }

        const promises = lemma?.meanings.map((meaning) => this.migrateMeaning(meaning, signVideo)) ?? []
        await Promise.all(promises)
    }

    private async migrateMeaning(meaning: LexMeaning, signVideo: SignVideo) {
        let term = this.getProjectTerm(meaning.lexicalLink)
        if (!term) {
            term = this.createProjectTerm(meaning.lexicalLink)
            await this.addProjectTerm(term)
            const _term = this.getProjectTerm(term.lexicalLink)
            if (!_term) {
                return
            }
            term = _term
        }

        // Only migrate rendering if it hasn't been done before
        if (term.renderings.find((r) => r.url === signVideo.url)) {
            return
        }

        const rendering = term.createRendering()
        rendering.url = signVideo.url
        await term.addRendering(rendering)

        // Do not delete the old renderings. If someone is still using an
        // old version of xxTT, it will look like their data was lost.
    }

    getKeyTermsThatOccurInVerses(refRanges: RefRange[]) {
        const uniqueVerses = RefRange.getAllVersesInRefRanges(refRanges, this.versification)
        const originalVerses = convertReferenceStrings(uniqueVerses, this.versification, 'Original')
        const terms = new Set<ProjectTerm>()
        for (const verse of originalVerses) {
            const newTerms = this.termCollection.getProjectTermsByVerse(verse)
            for (const term of newTerms) {
                if (term.isKeyTerm) {
                    terms.add(term)
                }
            }
        }

        const termsArray = Array.from(terms)
        return termsArray
    }

    getFormattedDisplayName = () => (this.name === this.displayName ? this.name : `${this.displayName} (${this.name})`)

    createProjectMessage() {
        const creationDate = this.db.getNewId(this.messages, new Date(Date.now()))
        const _id = `notify/${creationDate}`
        return new ProjectMessage(_id, this.db)
    }

    async addProjectMessage(notif: ProjectMessage) {
        const { name } = this
        const { subject, globalMessage } = notif
        log('addProjectMessage', fmt({ project: name, subject, globalMessage }))

        await this.db.put(notif.toDocument())
    }

    async removeProjectMessage(notif: ProjectMessage) {
        const doc = notif._toDocument({ parent: notif.parent })
        doc.removed = true
        await notif.db.put(doc)
    }

    async setCopyrightStatement(copyrightStatement: string) {
        const trimmedCopyright = copyrightStatement.trim()
        if (this.copyrightStatement === trimmedCopyright) {
            return
        }

        const doc = this.toDocument()
        doc.copyrightStatement = trimmedCopyright
        await this.db.put(doc)
    }

    // _id can be for a portion or any of its subobjects (e.g. Passage)
    findPortion(_id: string) {
        return this.portions.find((p) => _id.startsWith(p._id))
    }

    // _id can be for a passage or any of its subobjects (e.g. PassageVideo)
    findPassage(_id: string) {
        const portion = this.findPortion(_id)
        return portion?.passages.find((p) => _id.startsWith(p._id))
    }

    async setNoteColors(noteColors: string[]) {
        if (JSON.stringify(noteColors) === JSON.stringify(this.noteColors)) {
            return
        }
        const doc = this._toDocument({ model: 1, noteColors })
        doc._id = 'teamPreferences'
        await this.db.put(doc)
    }

    async setDateFormat(dateFormat: DateFormat) {
        if (dateFormat === this.dateFormat) {
            return
        }
        const doc = this._toDocument({ model: 1, dateFormat })
        doc._id = 'teamPreferences'
        await this.db.put(doc)
    }

    async setCompressedVideoQuality(compressedVideoCRF: number) {
        if (compressedVideoCRF === this.compressedVideoQuality) {
            return
        }
        const doc = this.toDocument()
        doc.model = 9
        doc.compressedVideoCRF = compressedVideoCRF
        await this.db.put(doc)
    }

    async setCompressedVideoResolution(compressedVideoResolution: number) {
        if (compressedVideoResolution === this.compressedVideoResolution) {
            return
        }
        const doc = this.toDocument()
        doc.model = 9
        doc.compressedVideoResolution = compressedVideoResolution
        await this.db.put(doc)
    }

    async setMaxVideoSizeMB(maxVideoSizeMB: number) {
        if (maxVideoSizeMB === this.maxVideoSizeMB) {
            return
        }
        const doc = this.toDocument()
        doc.model = 9
        doc.maxVideoSizeMB = maxVideoSizeMB
        await this.db.put(doc)
    }

    async setRecordAudioOnly(recordAudioOnly: boolean) {
        if (recordAudioOnly === this.recordAudioOnly) {
            return
        }
        const doc = this.toDocument()
        doc.model = 14
        doc.recordAudioOnly = recordAudioOnly
        await this.db.put(doc)
    }

    async setDisplayName(displayName: string) {
        if (displayName === this.displayName || displayName.trim() === '') {
            return
        }

        const doc = this._toDocument({ model: 3, displayName })
        doc._id = 'projectPreferences'
        await this.db.put(doc)
    }

    async setText(text: string) {
        if (text === this.text) {
            return
        }

        const doc = this._toDocument({ model: 3, text })
        doc._id = 'projectPreferences'
        await this.db.put(doc)
    }

    async setProjectType(projectType: string) {
        if (projectType === this.projectType) {
            return
        }

        const doc = this._toDocument({ model: 3, projectType })
        doc._id = 'projectPreferences'
        await this.db.put(doc)
    }

    async setRegion(region: string) {
        if (region === this.region) {
            return
        }

        const doc = this._toDocument({ model: 3, region })
        doc._id = 'projectPreferences'
        await this.db.put(doc)
    }

    async setOverrideRecordingLayout(overrideRecordingLayout: boolean) {
        if (overrideRecordingLayout === this.overrideRecordingLayout) {
            return
        }

        const doc = this._toDocument({
            _id: 'projectPreferences',
            model: 20,
            overrideRecordingLayout
        })
        await this.db.put(doc)
    }

    async setSegmentEditorPanelLayout(segmentEditorPanelLayout: SegmentEditorPanel[]) {
        if (JSON.stringify(segmentEditorPanelLayout) === JSON.stringify(this.segmentEditorPanelLayout)) {
            return
        }

        const doc = this._toDocument({
            _id: 'projectPreferences',
            model: 20,
            segmentEditorPanelLayout
        })
        await this.db.put(doc)
    }

    async setSegmentEditorPanelVisibility(segmentEditorPanel: SegmentEditorPanel, visible: boolean) {
        const hiddenPanels = [...this.segmentEditorHiddenPanels]

        if (visible) {
            const index = hiddenPanels.indexOf(segmentEditorPanel)
            if (index === -1) {
                return
            }
            hiddenPanels.splice(index, 1)
        } else {
            if (hiddenPanels.includes(segmentEditorPanel)) {
                return
            }
            hiddenPanels.push(segmentEditorPanel)
        }

        const doc = this._toDocument({
            _id: 'projectPreferences',
            model: 20,
            segmentEditorHiddenPanels: hiddenPanels
        })
        await this.db.put(doc)
    }

    async addImage(src: string) {
        src = src.trim()
        if (src === '' || this.images.find((image) => image.src === src)) {
            return
        }
        const newId = this.db.getNewId(this.images, new Date(Date.now()), 'prjImg_')
        const image = new ProjectImage(newId, this.db)
        image.src = src

        let rank = 100
        if (this.images.length > 0) {
            rank = this.images[0].rankAsNumber / 2
        }
        image.rank = DBObject.numberToRank(rank)
        await this.db.put(image.toDocument())
    }

    async deleteImage(_id: string) {
        await remove(this.images, _id)
    }

    async moveImage(_id: string, i: number) {
        const idx = _.findIndex(this.images, { _id })
        if (idx === i) {
            return
        }

        if (idx === -1) throw Error(`moveProjectIcon: _id not found [${_id}]`)
        await move(this.images, idx, i)
    }

    *passageVideos() {
        for (const portion of this.portions) {
            for (const passage of portion.passages) {
                for (const video of passage.videos) {
                    if (video.removed) continue
                    yield video
                }
            }
        }
    }

    countAll() {
        let [portions, passages, videos, noteItems, glosses] = [0, 0, 0, 0, 0]

        for (const portion of this.portions) {
            ++portions
            for (const passage of portion.passages) {
                ++passages
                for (const video of passage.videos) {
                    if (video.removed) continue
                    ++videos
                    for (const note of video.notes) {
                        noteItems += note.items.length
                    }
                    glosses += video.glosses.length
                }
            }
        }

        return { name: this.name, portions, passages, videos, noteItems, glosses }
    }

    videod() {
        return this.portions.some((portion) => portion.passages.some((passage) => passage.videod()))
    }
}
